/**
 * Merced College brand colors — THIS IS MERCED COLLEGE (mccd.edu), NOT UC Merced.
 *
 * Two first-party sources, agreeing (owner-confirmed via the driver, 2026-09-05):
 *  1. Merced College Brand Guide
 *     (https://www.mccd.edu/about-merced-college/divisions/external-relations/brand-guide/):
 *       MC Blue  PMS 281  #00205C (RGB 0 32 92)    — primary
 *       MC Gold  PMS 1245 #C38F00 (RGB 195 143 0)  — accent
 *     Tints #F5B400 / #F5C749; secondary #0F61D8 / #D5E5E8 / #9DC791 / #ED6348;
 *     neutrals #181B21 / #888888 / #F7F3EB / #EEEEEE.
 *  2. The college's own Coursedog catalog theme (S0 capture
 *     admin_tools/backend/data/onboarding/merced-college/probes/course_index.html):
 *     --theme:#00205C, --theme-lighter:#C38F00, --theme-darker:#E8B538
 *     (topbar elements), --h3HeaderFontColor:#8F6900, logo asset
 *     MCC_Logo_Gold-Stacked-small.png.
 *
 * Contrast (WCAG, measured): MC Blue on white 15.43:1 — white text passes
 * easily, so `-strong` is the base brand color (v8.13 token rules, UCM
 * precedent). MC Gold on white 2.90:1 (fails AA for normal text — the same
 * class as UMD gold and UC Merced's Bobcat Gold #DAA900 at ~2.2:1), on the
 * warm charcoal #161412 6.34:1 (AA). Kept as the accent per the owner's
 * brand confirm; the brand's own deeper gold #8F6900 (5.01:1 white / 3.66:1
 * dark) is the two-surface compromise if `text-accent` legibility on light
 * surfaces is ever prioritised (the SDSU Dark-Teal pattern) — it is used
 * here for the accent-700 step and the light mark's oblique face.
 *
 * Brand mark (components/ui/BrandMark.tsx): the mark is GOLD, not navy —
 * deliberately. UC Merced's mark front is Bobcat Blue 0 40 86; MC Blue
 * 0 32 92 is 10.0 RGB units away, far under the 50-unit cross-theme floor in
 * BrandMark.test.tsx, and the two institutions share a city — a navy mark
 * would make the two headers indistinguishable (the exact W48 hazard). The
 * gold mark is first-party (the catalog ships the gold stacked logo and
 * gold topbar elements). Light front MC Gold 195 143 0 (109 from UMD red,
 * 130 from SDSU dark red, 246 from SJSU blue, 252 from UCM navy); dark front
 * tint #F5B400 245 180 0 (135 from UMD dm, 169 from SDSU dm).
 *
 * The 50-900 ladders are HSL interpolations around the brand values (brand
 * at `700`/DEFAULT for the navy, `600`/DEFAULT for the gold) — brand-aligned
 * for UI use, not an officially published extended palette. Logo policy
 * mirrors UMD/SJSU/UCM/SDSU: text-only branding, no logo asset embedded.
 */
import type { ThemeColors } from '../types';

export const mercedCollegeColors: ThemeColors = {
  // Primary — MC Blue (PMS 281, #00205C = rgb(0, 32, 92))
  '--color-primary':     '0 32 92',
  // -strong: very dark navy — white text passes at 15.43:1, so -strong is
  // the base brand color.
  '--color-primary-strong': '0 32 92',
  '--color-primary-50': '235 242 255',
  '--color-primary-100': '209 225 255',
  '--color-primary-200': '163 195 255',
  '--color-primary-300': '102 155 255',
  '--color-primary-400': '31 109 255',
  '--color-primary-500': '0 78 224',
  '--color-primary-600': '0 60 173',
  '--color-primary-700': '0 32 92',
  '--color-primary-800': '0 26 74',
  '--color-primary-900': '0 20 56',

  // Accent — MC Gold (PMS 1245, #C38F00 = rgb(195, 143, 0)); see header.
  '--color-accent':     '195 143 0',
  '--color-accent-50': '255 250 235',
  '--color-accent-100': '255 243 209',
  '--color-accent-200': '255 231 163',
  '--color-accent-300': '255 214 102',
  '--color-accent-400': '255 195 31',
  '--color-accent-500': '224 165 0',
  '--color-accent-600': '195 143 0',
  '--color-accent-700': '143 105 0',
  '--color-accent-800': '92 67 0',
  '--color-accent-900': '56 41 0',

  // Brand-mark faces — GOLD (see header). Light: MC Gold front, the
  // catalog's h3 gold #8F6900 as the top face, a deeper step for the side.
  // Dark: brand tint #F5B400 front, stepping down through MC Gold to #8F6900.
  '--color-mark-front': '195 143 0',
  '--color-mark-top':   '143 105 0',
  '--color-mark-side':  '100 73 0',
  '--color-mark-front-dm': '245 180 0',
  '--color-mark-top-dm':   '195 143 0',
  '--color-mark-side-dm':  '143 105 0',
};
